<template>
  <ShiciGameLayout>
    <div class="backup-container">
      <!-- 顶部操作栏 -->
      <div class="backup-header">
        <h2 class="backup-title">诗词背诵</h2>
        <div class="header-actions">
          <el-input
            v-model="searchKeyword"
            placeholder="搜索诗词标题或作者"
            prefix-icon="el-icon-search"
            clearable
            @keyup.enter="handleSearch"
            style="width: 300px; margin-right: 10px;"
          />
          <el-button type="primary" @click="handleSearch">搜索</el-button>
          <el-button type="success" @click="getRandomShici">随机提取</el-button>
        </div>
      </div>

      <!-- 三步指示器 -->
      <div class="step-indicator">
        <div class="step-btn" :class="{ active: currentStep === 1 }" @click="currentStep = 1; handleStepChange()">
          <span class="step-num">1</span>
          <span class="step-name">跟读</span>
        </div>
        <div class="step-line"></div>
        <div class="step-btn" :class="{ active: currentStep === 2 }" @click="currentStep = 2; handleStepChange()">
          <span class="step-num">2</span>
          <span class="step-name">猜句</span>
        </div>
        <div class="step-line"></div>
        <div class="step-btn" :class="{ active: currentStep === 3 }" @click="currentStep = 3; handleStepChange()">
          <span class="step-num">3</span>
          <span class="step-name">填空</span>
        </div>
      </div>

      <!-- 主内容区域 -->
      <div class="backup-content">
        <!-- 左侧诗文内容 -->
        <div class="content-left">
          <div v-if="!currentShici" class="empty-state">
            <el-icon class="empty-icon"><document /></el-icon>
            <p>请随机提取或搜索一首诗词开始背诵</p>
          </div>
          <div v-else class="shici-content">
            <h3 class="shici-title">{{ currentShici.title }}</h3>
            <p class="shici-author">{{ currentShici.author }}</p>
            
            <!-- 第一步：跟读 -->
            <div v-if="currentStep === 1" class="step-content">
              <div class="reading-content">
                <span
                  v-for="(char, index) in shiciChars"
                  :key="index"
                  :class="{
                    'char-normal': index < currentCharIndex,
                    'char-highlight': index === currentCharIndex,
                    'char-future': index > currentCharIndex
                  }"
                >{{ char }}</span>
              </div>
              <div class="reading-controls">
                <el-button @click="startReading" v-if="!isReading">开始跟读</el-button>
                <el-button @click="pauseReading" v-else>暂停</el-button>
                <el-button @click="resetReading">重置</el-button>
              </div>
            </div>

            <!-- 第二步：猜句 -->
            <div v-if="currentStep === 2" class="step-content">
              <div class="guess-content">
                <div
                  v-for="(sentence, sentenceIndex) in shiciSentences"
                  :key="sentenceIndex"
                  class="guess-line"
                  :class="{ 'sentence-hidden': (sentenceIndex + startWithFirst) % 2 !== 0 }"
                >
                  <template v-if="(sentenceIndex + startWithFirst) % 2 === 0">{{ sentence }}</template>
                  <template v-else>
                    <span
                      v-for="(char, charIndex) in sentence.split('')"
                      :key="charIndex"
                      class="hidden-underline"
                      :class="{ 'hint-char': guessedChars[sentenceIndex] && guessedChars[sentenceIndex] > charIndex }"
                    >{{ guessedChars[sentenceIndex] && guessedChars[sentenceIndex] > charIndex ? char : (isPunctuation(char) ? char : '_') }}</span>
                  </template>
                </div>
              </div>
            </div>

            <!-- 第三步：填空 -->
            <div v-if="currentStep === 3" class="step-content">
              <div class="fill-content">
                <div
                  v-for="(sentence, sentenceIndex) in shiciSentences"
                  :key="sentenceIndex"
                  class="fill-line"
                >
                  <span
                    v-for="(char, charIndex) in sentence.split('')"
                    :key="charIndex"
                    class="hidden-underline"
                    :class="{ 'hint-char': guessedChars[ sentenceIndex + '_' + charIndex] }"
                  >{{ guessedChars[ sentenceIndex + '_' + charIndex] ? char : (isPunctuation(char) ? char : '_') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 右侧操作选项悬浮窗 -->
        <div class="options-float">
          <div class="options-panel">
            <h4>选项配置</h4>

            <!-- 第一步选项 -->
            <div v-if="currentStep === 1" class="step-options">
              <div class="option-item">
                <span class="option-label">字间停顿时间</span>
                <div class="time-presets">
                  <el-button
                    v-for="time in [200, 400, 600, 800]"
                    :key="time"
                    :type="charInterval === time ? 'primary' : 'default'"
                    size="small"
                    @click="charInterval = time"
                    class="time-btn"
                  >{{ time }}ms</el-button>
                </div>
                <el-slider v-model="charInterval" :min="100" :max="1000" :step="100" show-input />
              </div>
            </div>

            <!-- 第二步选项 -->
            <div v-if="currentStep === 2" class="step-options">
              <div class="option-item">
                <span class="option-label">第一个隐藏</span>
                <div class="time-presets">
                  <el-button
                    :type="startWithFirst === 0 ? 'primary' : 'default'"
                    size="small"
                    @click="startWithFirst = 0"
                    class="time-btn"
                  >第一句</el-button>
                  <el-button
                    :type="startWithFirst === 1 ? 'primary' : 'default'"
                    size="small"
                    @click="startWithFirst = 1"
                    class="time-btn"
                  >第二句</el-button>
                </div>
              </div>
              <div class="option-item">
                <el-button type="info" @click="showHint" class="big-btn hint-btn-top">提示</el-button>
              </div>
            </div>

            <!-- 第三步选项 -->
            <div v-if="currentStep === 3" class="step-options">
              <div class="option-item">
                <el-button type="info" @click="showFillHint" class="big-btn hint-btn-top">提示</el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 搜索结果对话框 -->
      <el-dialog
        :visible.sync="showSearchResults"
        title="选择诗词"
        width="500px"
      >
        <div class="search-results">
          <div
            v-for="(shici, index) in searchResults"
            :key="index"
            class="search-result-item"
            @click="selectShici(shici)"
          >
            <h3>{{ shici.title || shici.Title || '无标题' }}</h3>
            <p>{{ shici.author || shici.Author || '无作者' }}</p>
            <p class="content-preview">{{ (shici.content || shici.Content || '').substring(0, 50) }}...</p>
          </div>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="showSearchResults = false">取消</el-button>
        </span>
      </el-dialog>
    </div>
  </ShiciGameLayout>
</template>

<script>
import ShiciGameLayout from './ShiciGameLayout'
import axios from 'axios'

export default {
  name: 'shicibackup',
  components: {
    ShiciGameLayout
  },
  data() {
    return {
      // 诗词数据
      currentShici: null,
      shiciChars: [],
      shiciLines: [],
      shiciSentences: [],
      
      // 搜索
      searchKeyword: '',
      searchResults: [], // 搜索结果列表
      showSearchResults: false, // 显示搜索结果对话框
      
      // 背诵步骤
      currentStep: 1,
      
      // 第一步：跟读
      currentCharIndex: 0,
      isReading: false,
      readingTimer: null,
      charInterval: 500, // 字间停顿时间（毫秒）
      
      // 第二步：猜句
      startWithFirst: 0, // 0: 第一句开始隐藏，1: 第二句开始隐藏
      guessedChars: {}, // 已猜的字
      
      // 第三步：填空
      filledChars: {}, // 已填的字
      
      // API 配置
      UseUrl: '/api'
    }
  },
  mounted() {
    this.getRandomShici()
  },
  methods: {
    // 获取随机诗词
    async getRandomShici() {
      try {
        const token = localStorage.getItem('shicigame_token')
        const response = await axios.post(this.UseUrl + '/shicigame/random', {
          count: 1,
          types: []
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        if (response.data && response.data.shiciList && response.data.shiciList.length > 0) {
          this.currentShici = response.data.shiciList[0]
          this.initShiciData()
        }
      } catch (error) {
        console.error('获取随机诗词失败:', error)
        this.$message.error('获取诗词失败')
      }
    },
    
    // 搜索诗词
    async handleSearch() {
      if (!this.searchKeyword) {
        this.getRandomShici()
        return
      }
      
      try {
        const token = localStorage.getItem('shicigame_token')
        
        const response = await axios.post(this.UseUrl + '/shicigame/list', {
          page: 1,
          pageSize: 10,
          favoriteOnly: false,
          title: this.searchKeyword,
          author: '',
          dynasty: '',
          content: ''
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        const list = response.data?.list || response.data?.List || response.data?.data || []
        
        if (list && list.length > 0) {
          if (list.length === 1) {
            const item = list[0]
            this.currentShici = {
              title: item.Title || item.title,
              author: item.Author || item.author,
              content: item.Content || item.content,
              id: item.ID || item.id,
              annotation: item.Annotation || item.annotation,
              createTime: item.CreateTime || item.createTime,
              favorite: item.Favorite || item.favorite,
              priority: item.Priority || item.priority
            }
            this.initShiciData()
          } else {
            this.searchResults = list.slice(0, 5)
            setTimeout(() => {
              this.showSearchResults = true
            }, 100)
          }
        } else {
          this.$message.info('未找到匹配的诗词')
        }
      } catch (error) {
        console.error('搜索诗词失败:', error)
        this.$message.error('搜索失败')
      }
    },
    
    // 初始化诗词数据
    initShiciData() {
      if (this.currentShici) {
        // 拆分为字符数组
        this.shiciChars = this.currentShici.content.split('')
        // 按换行符分割得到行
        const lines = this.currentShici.content.split('\n')
        // 将每行按标点符号分割，合并成一个句子数组，保留标点符号
        this.shiciSentences = []
        lines.forEach(line => {
          // 保留标点符号的分割
          let current = ''
          for (let i = 0; i < line.length; i++) {
            const char = line[i]
            current += char
            // 遇到标点符号，分割句子
            if ('，。！？、；：'.includes(char)) {
              this.shiciSentences.push(current)
              current = ''
            }
          }
          // 处理最后一句（可能没有标点）
          if (current.trim()) {
            this.shiciSentences.push(current)
          }
        })
        // 重置状态
        this.resetAllSteps()
      }
    },
    
    // 选择搜索结果
    selectShici(shici) {
      // 转换字段名，兼容大小写
      this.currentShici = {
        title: shici.title || shici.Title || '',
        author: shici.author || shici.Author || '',
        content: shici.content || shici.Content || '',
        id: shici.id || shici.ID || '',
        annotation: shici.annotation || shici.Annotation || '',
        createTime: shici.createTime || shici.CreateTime || 0,
        favorite: shici.favorite || shici.Favorite || false,
        priority: shici.priority || shici.Priority || 1
      }
      this.initShiciData()
      this.showSearchResults = false
    },
    
    // 重置所有步骤
    resetAllSteps() {
      // 第一步
      this.currentCharIndex = 0
      this.isReading = false
      if (this.readingTimer) {
        clearInterval(this.readingTimer)
        this.readingTimer = null
      }
      
      // 第二步
      this.guessedChars = {}
      
      // 第三步
      this.filledChars = {}
      this.fillCurrentLine = 0
      this.fillCurrentChar = 0
    },
    
    // 开始跟读
    startReading() {
      if (!this.currentShici) return
      
      this.isReading = true
      this.currentCharIndex = 0
      
      this.readingTimer = setInterval(() => {
        if (this.currentCharIndex < this.shiciChars.length) {
          this.currentCharIndex++
        } else {
          this.pauseReading()
        }
      }, this.charInterval)
    },
    
    // 暂停跟读
    pauseReading() {
      this.isReading = false
      if (this.readingTimer) {
        clearInterval(this.readingTimer)
        this.readingTimer = null
      }
    },
    
    // 重置跟读
    resetReading() {
      this.pauseReading()
      this.currentCharIndex = 0
    },
    
    // 显示猜句提示
    showHint() {
      if (!this.currentShici) return

      // 找到第一个还有未显示字符的隐藏句子
      for (let sentenceIndex = 0; sentenceIndex < this.shiciSentences.length; sentenceIndex++) {
        // 只处理隐藏的句子
        if ((sentenceIndex + this.startWithFirst) % 2 !== 0) {
          const sentence = this.shiciSentences[sentenceIndex]
          const chars = sentence.split('')
          let revealedCount = this.guessedChars[sentenceIndex] || 0

          // 跳过标点符号，找到下一个真正需要提示的字符位置
          while (revealedCount < chars.length && this.isPunctuation(chars[revealedCount])) {
            revealedCount++
          }

          if (revealedCount < chars.length) {
            this.$set(this.guessedChars, sentenceIndex, revealedCount + 1)
            return
          }
        }
      }

      this.$message.info('已经没有更多字符可以提示了')
    },
    
    // 重置猜句
    resetGuess() {
      // 清除所有第二步的提示
      for (let key in this.guessedChars) {
        if (!key.includes('_')) {
          delete this.guessedChars[key]
        }
      }
    },
    
    // 获取隐藏文本
    getHiddenText(text) {
      return text.split('').map(char => char === ' ' ? ' ' : '_').join('')
    },
    
    // 获取显示的字符
    getDisplayChar(char, sentenceIndex, charIndex) {
      // 判断当前字符是否应该隐藏
      const isOddSentence = (sentenceIndex + this.startWithFirst) % 2 !== 0
      const isOddChar = charIndex % 2 !== 0
      
      // 奇数句子：奇数字符隐藏
      // 偶数句子：偶数字符隐藏
      const shouldHide = isOddSentence ? isOddChar : !isOddChar
      
      return shouldHide ? '_' : char
    },
    
    // 判断是否是标点符号
    isPunctuation(char) {
      const punctuations = '，。！？、；：（）【】《》—…·～「」『』'
      return punctuations.indexOf(char) !== -1
    },
    
    // 显示填空提示
    showFillHint() {
      if (!this.currentShici) return

      // 遍历所有隐藏的字
      for (let sentenceIndex = 0; sentenceIndex < this.shiciSentences.length; sentenceIndex++) {
        const sentence = this.shiciSentences[sentenceIndex]
        const chars = sentence.split('')

        for (let charIndex = 0; charIndex < chars.length; charIndex++) {
          const key = sentenceIndex + '_' + charIndex
          const char = chars[charIndex]
          // 跳过标点符号
          if (this.isPunctuation(char)) continue
          // 找到第一个没有显示的非标点字符
          if (!this.guessedChars[key]) {
            this.$set(this.guessedChars, key, char)
            return
          }
        }
      }

      this.$message.info('已经没有更多字符可以提示了')
    },
    
    // 重置填空
    resetFill() {
      // 清除所有第三步的提示
      for (let key in this.guessedChars) {
        if (key.includes('_')) {
          delete this.guessedChars[key]
        }
      }
    },
    
    // 步骤切换
    handleStepChange() {
      this.resetAllSteps()
    },
    
    // 上一步
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--
        this.resetAllSteps()
      }
    },
    
    // 下一步
    nextStep() {
      if (this.currentStep < 3) {
        this.currentStep++
        this.resetAllSteps()
      }
    }
  }
}
</script>

<style scoped>
.backup-container {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  padding: 30px;
}

.backup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f0f0f0;
}

.backup-title {
  font-size: 28px;
  font-weight: 600;
  color: #667eea;
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* 三步指示器 */
.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  padding: 15px 0;
}

.step-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: #f0f0f0;
  color: #909399;
  cursor: pointer;
  transition: all 0.3s ease;
}

.step-btn.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.step-btn:hover {
  transform: scale(1.1);
}

.step-num {
  font-size: 24px;
  font-weight: 600;
}

.step-name {
  font-size: 12px;
  margin-top: 2px;
}

.step-line {
  width: 60px;
  height: 3px;
  background: #e0e0e0;
  margin: 0 10px;
}

.backup-content {
  display: flex;
  gap: 30px;
  min-height: 500px;
}

.content-left {
  flex: 2;
  background: #f9f9f9;
  border-radius: 8px;
  padding: 30px;
  border: 1px solid #e0e0e0;
}

.options-float {
  position: fixed;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(245, 245, 245, 0.95);
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  max-width: 200px;
  max-height: 70vh;
  overflow-y: auto;
  z-index: 100;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  text-align: center;
  color: #909399;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 20px;
  color: #c0c4cc;
}

.shici-content {
  text-align: center;
}

.shici-title {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
}

.shici-author {
  font-size: 16px;
  color: #606266;
  margin-bottom: 30px;
}

.step-content {
  margin-top: 30px;
}

/* 第一步：跟读 */
.reading-content {
  font-size: 22px;
  line-height: 2.5;
  letter-spacing: 8px;
  margin-bottom: 30px;
  min-height: 200px;
  text-align: left;
}

.char-normal {
  color: #c0c4cc;
}

.char-highlight {
  color: #667eea;
  font-weight: 600;
}

.char-future {
  color: #606266;
}

.reading-controls {
  display: flex;
  gap: 15px;
  justify-content: center;
}

/* 第二步：猜句 */
.guess-content {
  text-align: left;
  line-height: 2;
  font-size: 20px;
}

.guess-line {
  margin-bottom: 15px;
}

.sentence-visible {
  color: #303133;
}

.sentence-hidden {
  color: #909399;
}

.guess-line .hidden-underline {
  display: inline-block;
  width: 1em;
  text-align: center;
}

.fill-line .hidden-underline {
  display: inline-block;
  width: 1em;
  text-align: center;
}

/* 搜索结果样式 */
.search-results {
  max-height: 400px;
  overflow-y: auto;
}

.search-result-item {
  padding: 15px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.3s;
}

.search-result-item:hover {
  border-color: #409eff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.search-result-item h3 {
  margin: 0 0 5px 0;
  font-size: 16px;
  color: #303133;
}

.search-result-item p {
  margin: 0 0 5px 0;
  color: #606266;
}

.content-preview {
  font-size: 14px;
  color: #909399;
  line-height: 1.4;
}
</style>

/* 第三步：填空 */
.fill-content {
  text-align: left;
  line-height: 2;
  font-size: 20px;
}

.fill-line {
  margin-bottom: 15px;
}

.filled-char {
  color: #667eea;
  font-weight: 600;
}

.sentence-hidden {
  color: #909399;
}

.options-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.options-panel h4 {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 10px 0;
  padding-bottom: 5px;
  border-bottom: 1px solid #e0e0e0;
}

.quick-actions {
  display: flex;
  gap: 15px;
}

.big-btn {
  flex: 1;
  padding: 15px 20px;
  font-size: 16px;
  border-radius: 8px;
}

.step-options {
  background: white;
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.option-item {
  margin-bottom: 15px;
}

.option-label {
  display: block;
  font-size: 14px;
  color: #606266;
  margin-bottom: 10px;
}

.time-presets {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.time-btn {
  min-width: 60px;
}

.option-desc {
  display: block;
  margin-top: 5px;
  color: #909399;
  font-size: 14px;
}

.shici-info {
  background: white;
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  margin-top: 20px;
}

.shici-info p {
  margin: 5px 0;
  font-size: 14px;
  color: #606266;
}

.shici-info p {
  margin: 5px 0;
  font-size: 14px;
  color: #606266;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .backup-container {
    padding: 10px;
  }
  
  .backup-header {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  
  .backup-title {
    font-size: 20px;
    text-align: center;
  }
  
  .header-actions {
    flex-direction: column;
    gap: 10px;
  }
  
  .header-actions .el-input {
    width: 100% !important;
  }
  
  .header-actions .el-button {
    width: 100%;
  }
  
  .step-indicator {
    padding: 10px 0;
  }
  
  .step-btn {
    min-width: 60px;
    padding: 8px 12px;
  }
  
  .step-num {
    width: 24px;
    height: 24px;
    line-height: 24px;
    font-size: 14px;
  }
  
  .step-name {
    font-size: 12px;
  }
  
  .backup-content {
    flex-direction: column;
  }
  
  .content-left {
    padding: 15px;
  }
  
  .shici-title {
    font-size: 20px;
    text-align: center;
  }
  
  .shici-author {
    font-size: 14px;
    text-align: center;
  }
  
  .reading-content {
    font-size: 16px;
    line-height: 2;
    letter-spacing: 2px;
  }
  
  .char-highlight {
    font-size: 18px;
  }
  
  .guess-line {
    font-size: 16px;
    line-height: 1.8;
    margin-bottom: 10px;
  }
  
  .fill-line {
    font-size: 16px;
    line-height: 1.8;
    margin-bottom: 10px;
  }
  
  .hidden-underline {
    width: 0.8em;
  }
  
  .options-float {
    position: static;
    transform: none;
    max-width: 100%;
    margin-top: 15px;
    padding: 10px;
  }
  
  .options-panel h4 {
    font-size: 14px;
  }
  
  .step-options .option-item {
    margin-bottom: 10px;
  }
  
  .option-label {
    font-size: 12px;
  }
  
  .big-btn {
    width: 100%;
    padding: 10px;
    font-size: 14px;
  }
  
  .time-presets {
    flex-wrap: wrap;
  }
  
  .time-btn {
    padding: 5px 10px;
    font-size: 12px;
  }
  
  .hint-btn-top {
    margin-bottom: 10px;
  }
  
  .el-slider {
    margin: 10px 0;
  }
  
  .search-results {
    max-height: 300px;
  }
  
  .search-result-item {
    padding: 10px;
  }
  
  .search-result-item h3 {
    font-size: 14px;
  }
}
</style>